import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportWinners, getRecognitionErrorMessage } from '@/api/recognition';

interface Props {
  file: File;
  modo: 'vigente' | 'historica';
  campaignId: string;
  badgeId: string;
  onDone: () => void;
}

/**
 * Paso 4 (final): re-envía el MISMO archivo/config con `preview: false` — esta vez SÍ escribe. El
 * resultado se muestra de forma PERSISTENTE (sin el auto-dismiss de 8 s que tenía el panel de
 * validación anterior): si algo falló, el administrador necesita poder leerlo con calma, no que
 * desaparezca solo.
 */
export function StepConfirm({ file, modo, campaignId, badgeId, onDone }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const commit = useImportWinners();
  const yaEnviado = useRef(false);

  useEffect(() => {
    // 🔴 Mismo defecto que StepReview: disparar `mutate()` en el mismo tick del efecto de montaje
    // deja la mutación resuelta (fetch/onSuccess/onSettled corren bien) pero SIN re-render, porque
    // la suscripción de `useMutation` a este observador todavía no queda enganchada en ese commit.
    // Diferir al siguiente macrotask lo deja indistinguible de una interacción posterior (un clic),
    // que sí actualiza. La marca de "ya enviado" va DENTRO del timeout, no antes de programarlo: en
    // Strict Mode (mount → cleanup → mount) el primer `setTimeout` se cancela en el cleanup antes
    // de disparar, y si la marca ya estuviera puesta el segundo paso (el que se queda montado de
    // verdad) jamás programaría el suyo.
    const id = setTimeout(() => {
      if (yaEnviado.current) return;
      yaEnviado.current = true;
      commit.mutate({
        file, modo,
        campaignId: modo === 'vigente' ? campaignId : undefined,
        // La insignia es obligatoria en LOS DOS modos (ajuste de UX): antes solo viajaba en
        // histórica y un ganador vigente nacía sin insignia.
        badgeId,
        preview: false,
      });
    }, 0);
    return () => clearTimeout(id);
    // Se envía UNA vez al entrar a este paso; no debe repetirse en renders posteriores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const r = commit.data;

  return (
    <div className="bulk-step bulk-step--confirm">
      {commit.isPending && (
        <p role="status" className="bulk-status-pending">
          <i className="fas fa-spinner fa-spin" /> {t('admin.winners.importWizard.confirmPending')}
        </p>
      )}
      {commit.isError && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(commit.error, t('admin.winners.importError'))}
        </p>
      )}

      {r && (
        <>
          <p className={r.rechazados === 0 ? 'adm-success' : 'adm-error'} role={r.rechazados === 0 ? 'status' : 'alert'}>
            <i className={`fas ${r.rechazados === 0 ? 'fa-circle-check' : 'fa-triangle-exclamation'}`} aria-hidden />{' '}
            {t('admin.winners.importSummary', { importados: r.importados, total: r.totalFilas, rechazados: r.rechazados })}
          </p>

          {r.errores.length > 0 && (
            <div className="adm-table-wrap adm-table-wrap--spaced">
              <h4>{t('admin.winners.importErrorsTitle')}</h4>
              <table className="adm-table adm-table--glass">
                <thead>
                  <tr>
                    <th>{t('admin.winners.row')}</th>
                    <th>{t('admin.winners.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.errores.map((e, i) => (
                    <tr key={`${e.fila}-${e.codigo}-${i}`} className="adm-row--err">
                      <td>{e.fila === 0 ? t('admin.winners.importWizard.fileLevelError') : e.fila}</td>
                      <td>{e.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="bulk-wizard-nav">
        <span />
        <button type="button" className="btn btn--send" disabled={commit.isPending} onClick={onDone}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

export default StepConfirm;
